import { Maybe } from 'maybeasy';
import { Error } from '../../../../ErrorHandling';
import { SearchParameters } from '../../../../ListFilter/Types';
import { ParticipantsResource } from '../../../../ParticipantsStore/Types';
import { Resource } from '../../../../Resource/Types';

interface Waiting {
  id: number;
  kind: 'waiting';
  searchParameters: SearchParameters;
}

interface Loading {
  kind: 'loading';
  id: number;
  page: number;
  searchParameters: SearchParameters;
}

interface ReadyState {
  id: number;
  page: number;
  searchParameters: SearchParameters;
  resource: GroupParticipantsResource;
}

export interface Ready extends ReadyState {
  kind: 'ready';
}

export interface LoadingMore extends ReadyState {
  kind: 'loading-more';
  page: number;
}

export interface UpdatingQuery extends ReadyState {
  kind: 'updating-query';
  searchParameters: SearchParameters;
}

export interface UpdatingSearchParameters extends ReadyState {
  kind: 'updating-search-parameters';
  searchParameters: SearchParameters;
}

export interface Sorting extends ReadyState {
  kind: 'sorting';
  searchParameters: SearchParameters;
}

export const waiting = (id: number, searchParameters: SearchParameters): Waiting => ({
  kind: 'waiting',
  searchParameters,
  id,
});

export const loading = (state: Waiting | Sorting | Ready | UpdatingSearchParameters): Loading => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading',
    ...previous,
    page: 1,
  };
};

export const ready = (
  state: Loading | LoadingMore | UpdatingQuery | UpdatingSearchParameters | Ready,
  resource: GroupParticipantsResource,
): Ready => {
  const { kind, ...previous } = state;
  return {
    kind: 'ready',
    ...previous,
    resource,
  };
};

export const loadingMore = (state: Ready, page: number): LoadingMore => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading-more',
    ...previous,
    page,
  };
};

export const updatingSearchParameters = (
  searchParameters: SearchParameters,
  state: Ready,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters,
    page: 1,
  };
};

export const updatingQuery = (searchParameters: SearchParameters, state: Ready): UpdatingQuery => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-query',
    ...previous,
    searchParameters,
  };
};

export const sorting = (searchParameters: SearchParameters, state: Ready): Sorting => {
  const { kind, ...previous } = state;

  return {
    kind: 'sorting',
    ...previous,
    searchParameters,
  };
};

export type State =
  | Waiting
  | Loading
  | LoadingMore
  | Ready
  | UpdatingQuery
  | UpdatingSearchParameters
  | Sorting
  | Error;

export interface Participant {
  id: number;
  fullName: Maybe<string>;
  email: string;
  preferredLanguage: Maybe<string>;
  loginCount: number;
  lastLoginAt: Maybe<Date>;
  collections: string[];
  initials: Maybe<string>;
  userGroupsCount: number;
}

export type ParticipantResource = Resource<Participant>;

export interface GroupParticipants {
  kind: 'group-participants';
  id: number;
  name: string;
  updatedAt: Date;
  lastLoginAt: Maybe<Date>;
  status: string;
  participants: ParticipantsResource;
  totalParticipantsCount: number;
  allParticipantIds: number[];
  activatedLicenseCount: number;
}

export type GroupParticipantsResource = Resource<GroupParticipants>;

export type SortableColumn =
  | 'last_name'
  | 'email'
  | 'preferred_language'
  | 'sign_in_count'
  | 'last_sign_in_at';
